/* ══════════ Zustand des Starts ══════════
   Die eine Stelle, die sagt, was der Bildschirm gerade zeigen darf.

   ── Warum das nötig war ──────────────────────────────────────────────────
   Vorher gab es nur `signedIn` (true/false) im Konto-Modul und ein paar
   `classList.add('hidden')`-Aufrufe, verteilt über main.js, sync.js und
   screen.js. „Nicht angemeldet" und „wissen wir noch nicht" waren derselbe
   Zustand – und weil das Sitzungs-Cookie HttpOnly ist, dauert „wissen wir
   noch nicht" eine ganze Netzanfrage lang.

   Ein Zustand, der zwei Dinge bedeutet, führt zwangsläufig dazu, dass einer
   der beiden falsch dargestellt wird. Genau das war der Flacker-Effekt.

   ── Die drei Zustände ────────────────────────────────────────────────────
     unbekannt  – /auth/me läuft noch. Weder App noch Anmeldeformular.
                  Sichtbar ist der Startbildschirm mit einem Platzhalter.
     gast       – keine Sitzung. Anmeldeformular.
     drin       – Sitzung bestätigt. App.

   Dazu die Frage, ob die App ihre Daten schon hat:
     laedt      – Gerüst steht, Inhalte sind Skeletons.
     bereit     – echte Daten.

   Beides landet als `data-`Attribut am <body>. Damit entscheidet CSS, was zu
   sehen ist – nicht ein Dutzend Stellen in JavaScript. Ein einziges Attribut
   kann nicht „halb" gesetzt sein, und es gibt keine Reihenfolge, in der zwei
   Ansichten gleichzeitig sichtbar wären. */

export const AUTH_UNBEKANNT = 'unbekannt';
export const AUTH_VERMUTET = 'vermutet';
export const AUTH_GAST = 'gast';
export const AUTH_DRIN = 'drin';

/* ── „vermutet": die App-Hülle zeigen, bevor der Server geantwortet hat ───
   Beim Neuladen als angemeldete Person war für einen Moment der
   Anmeldebildschirm zu sehen. Nicht wegen der Netzanfrage – die dauert rund
   50 ms – sondern weil die Frage überhaupt erst gestellt wurde, nachdem die
   lokale Datenbank offen und der Lernstand gelesen war. Das dauert auf einem
   vollen Konto knapp eine Sekunde, und die ganze Zeit stand da der falsche
   Bildschirm.

   Zwei Dinge beheben das: die Frage zuerst stellen (siehe main.js) – und
   diesen Zustand hier, der die verbleibenden Millisekunden überbrückt.

   ── Was das NICHT ist ────────────────────────────────────────────────────
   Kein Anmeldezustand und keine Berechtigung. Der Merker sagt nur „hier war
   zuletzt jemand angemeldet" und entscheidet ausschließlich, WELCHER
   Ladebildschirm gezeigt wird. Er schaltet nichts frei, enthält kein Token
   und keine Adresse; wer ihn von Hand auf 1 setzt, sieht ein paar Skeletons
   und dann das Anmeldeformular. Über die Sitzung entscheidet weiterhin allein
   der Server – und da die Hülle in dieser Zeit nur Skeletons zeigt, sind auch
   im Irrtum keine Daten zu sehen. */
const MERKER = 'mimorio.zuletztDrin';
export function merkeSitzung(drin) {
  try {
    if (drin) localStorage.setItem(MERKER, '1');
    else localStorage.removeItem(MERKER);
  } catch { /* Speicher gesperrt – dann eben ohne Abkürzung */ }
}

let auth = AUTH_UNBEKANNT;
let daten = 'laedt';

export const authPhase = () => auth;
export const datenPhase = () => daten;

export function setzeAuthPhase(neu) {
  auth = neu;
  if (typeof document !== 'undefined' && document.body) document.body.dataset.auth = neu;
}

/* `laedt` heißt: die App ist sichtbar, aber ihre Zahlen stehen noch nicht
   fest. In dieser Zeit zeigen Seitenleiste und Übersicht Skeletons statt der
   Null-Werte aus dem HTML.

   Das ist kein Schönheitsproblem: der Lernstand kommt verschlüsselt vom
   Server und kann den lokalen Stand ersetzen. Was vor der Antwort in der
   Datenbank liegt, kann also zu einem anderen Konto gehören. Es anzuzeigen
   wäre nicht nur unruhig, sondern schlicht falsch. */
export function setzeDatenPhase(neu) {
  daten = neu;
  if (typeof document !== 'undefined' && document.body) document.body.dataset.app = neu;
}

/* Beim Start, noch bevor irgendetwas geladen ist. Das Attribut steht schon
   im HTML (siehe index.html) – hier wird es nur mit dem Modul gleichgezogen,
   falls die Datei je ohne das Attribut ausgeliefert wird. */
/* Beim Start NICHT zurücksetzen: das Inline-Skript in index.html hat den
   Anfangszustand bereits gesetzt – teils auf „vermutet". Ihn hier auf
   „unbekannt" zurückzudrehen würde die App-Hülle wieder gegen den
   Anmeldebildschirm tauschen, also genau das Flackern erzeugen, das die
   Abkürzung verhindern soll. Übernommen wird, was im Markup steht. */
export function initPhasen() {
  const da = document.body.dataset.auth;
  auth = da === AUTH_VERMUTET ? AUTH_VERMUTET : AUTH_UNBEKANNT;
  setzeAuthPhase(auth);
  setzeDatenPhase('laedt');
}
